import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from "@tanstack/react-table";
import '../../components_style/MyTable.css';
import SvGraphics from "../../assets/SvGraphics";
import '../../components_style/rtl_css/TreatmentTable.css';
import MoreTools from "./MoreTools";
import PropTypes from "prop-types";
import PaginationFilterTooltip from "./PaginationFilterTooltip";
import Pagination from "./Pagination";

const NoDataConst = () => (
    <div>
        <SvGraphics className={'block h-centered'} svgname={'no-search'} height={'300px'}/>
        <label className={'h-centered'} style={{top: '850%', fontSize: '40px'}}>No Available Data</label>
    </div>
);

const isRowEnabled = (row) => {
    return !((row.Enabled !== null && row.Enabled !== undefined) &&
        (row.Enabled === '0' || row.Enabled === 0));
};

function getFilteredData(field, statuses) {
    const language = localStorage.getItem('language');
    switch (field) {
        case "IsLicense":
            return [
                language === "he" ? "כן" : "Yes",
                language === "he" ? "לא" : "No",
            ];
        case "choose_programm":
            return [
                language === "he" ? "הוצאת רישיון חדש" : "New medical cannabis permit",
                language === "he" ? "חידוש רישיון למטופלים קיימים" : "Renew medical cannabis permit",
                language === "he" ? "חידוש רישיון למטופלים חדשים" : "License renewal for new patients",
                language === "he" ? "הוצאת מרשם חדש" : "New medical cannabis Prescription",
                language === "he" ? "חידוש מרשם למטופלים קיימים" : "Renew medical cannabis Prescription",
                language === "he" ? "חידוש מרשם למטופלים חדשים" : "Prescription renewal for new patients",
            ];
        case "resourceName":
            return ["לאומית", "איכילוב", "ללא"];
        case "requestStatus":
            return (statuses || []).map(item =>
                language === "he" ? item.status_desc_he : item.status_desc
            );
        case "insurance":
            return [
                language === "he" ? "מכבי" : "Maccabi",
                language === "he" ? "כללית" : "Clalit",
                language === "he" ? "לאומית" : "Leumit",
                language === "he" ? "מאוחדת" : "Meuhedet",
                language === "he" ? "חברת ביטוח" : "Insurance company",
                language === "he" ? "צה\"ל" : "IDF",
                language === "he" ? "משרד הביטחון" : "Ministry of Defence",
                language === "he" ? "ללא ביטוח" : "No Insurance",
                language === "he" ? "רשות פלסטינאית" : "The Palestinian Authority",
                language === "he" ? "אחר" : "Other",
            ];
        default:
            return [];
    }
}

function HeaderCell({ field, headerContent, makeFilter, thClass, filterConverter,
                       statuses, TabView, onFilterDataSelected, onOderByTable, style }) {
    const filterData = makeFilter ? getFilteredData(field, statuses) : [];
    const filterStyle = { width: '12px', height: '12px' };

    const handleClick = useCallback((e) => {
        if (onOderByTable) {
            localStorage.setItem('currentPage', 0);
            onOderByTable(headerContent);
        }
    }, [onOderByTable, headerContent]);

    return (
        <div className={`rt-th -cursor-pointer`} role="columnheader" tabIndex={-1} style={{ cursor: 'initial', ...style }}>
            <div
                onClick={handleClick}
                style={{ cursor: 'pointer' }}
                className={`${thClass || ''} rt-resizable-header-content`}
            >
                {headerContent}
            </div>
            {makeFilter && (
                <PaginationFilterTooltip
                    field={field}
                    filterStyle={filterStyle}
                    TabView={TabView}
                    filterData={filterData}
                    filterConverter={filterConverter}
                    onFilterDataSelected={filter => onFilterDataSelected(filter)}
                />
            )}
        </div>
    );
}

function NewPaginationTable(props) {
    const {
        data = [],
        columns: v6Columns,
        hasMore = false,
        hasShowInfo = false,
        hasSelection = false,
        moreFunctions = [],
        defaultPageSize: defaultPageSizeProp,
        rowsCount = 0,
        className: tableClassName,
        onFilterDataSelected,
        onOderByFunction,
        getRequest,
        TabView,
        statuses = [],
        showInfoBar,
        t,
        onSelectionChange,
    } = props;

    const defaultPageSize = defaultPageSizeProp || 14;
    const [selected, setSelected] = useState({});
    const [selectAll, setSelectAll] = useState(0);
    const [currentPage, setCurrentPage] = useState(() => {
        const stored = localStorage.getItem('currentPage');
        if (stored !== null) {
            localStorage.removeItem('currentPage');
            return parseInt(stored);
        }
        return 0;
    });

    useEffect(() => {
        const stored = localStorage.getItem('currentPage');
        if (stored !== null) {
            localStorage.removeItem('currentPage');
            setCurrentPage(parseInt(stored));
        }
    });

    const pageCount = rowsCount % defaultPageSize === 0
        ? rowsCount / defaultPageSize
        : parseInt(rowsCount / defaultPageSize) + 1;

    const showPagination = rowsCount > defaultPageSize;

    const toggleRow = useCallback((id) => {
        setSelected(prev => {
            const newSelected = { ...prev, [id]: !prev[id] };
            const selectedCount = Object.values(newSelected).filter(v => v).length;
            const enabledCount = data.filter(r => isRowEnabled(r)).length;
            setSelectAll(selectedCount !== enabledCount ? 1 : 2);
            if (onSelectionChange) onSelectionChange(newSelected);
            return newSelected;
        });
    }, [data, onSelectionChange]);

    const toggleAllRows = useCallback(() => {
        setSelected(prev => {
            const selectedCount = Object.values(prev).filter(v => v).length;
            const enabledCount = data.filter(r => isRowEnabled(r)).length;
            const allSelected = selectedCount === enabledCount && Object.values(prev).every(v => v);

            if (allSelected) {
                setSelectAll(1);
                if (onSelectionChange) onSelectionChange({});
                return {};
            } else {
                const newSelected = {};
                data.forEach(row => {
                    if (isRowEnabled(row)) newSelected[row.id] = true;
                });
                setSelectAll(2);
                if (onSelectionChange) onSelectionChange(newSelected);
                return newSelected;
            }
        });
    }, [data, onSelectionChange]);

    const columns = useMemo(() => {
        const cols = [];

        if (hasSelection) {
            cols.push({
                id: 'checkbox',
                header: () => (
                    <input type="checkbox" className="checkbox"
                        checked={selectAll === 2}
                        onChange={toggleAllRows}
                    />
                ),
                cell: ({ row }) => (
                    <input type="checkbox" className="checkbox"
                        disabled={!isRowEnabled(row.original)}
                        checked={selected[row.original.id] === true}
                        onChange={() => toggleRow(row.original.id)}
                    />
                ),
                size: 45,
                meta: { thClass: 'checkbox-th' },
            });
        }

        v6Columns.forEach(col => {
            cols.push({
                id: col.id || col.accessor,
                accessorKey: col.accessor || undefined,
                header: col.Header,
                cell: col.Cell
                    ? ({ row }) => col.Cell({ original: row.original })
                    : ({ getValue }) => getValue(),
                size: col.width || 150,
                meta: {
                    makeFilter: col.makeFilter,
                    thClass: col.thClass,
                    filterConverter: col.filterConverter,
                    className: col.className,
                },
            });
        });

        if (hasMore) {
            cols.push({
                id: 'more',
                header: '',
                cell: ({ row }) => {
                    const original = row.original;
                    let mf;
                    if (!isRowEnabled(original)) {
                        mf = moreFunctions.filter(f => f.name.indexOf('Lock ') === -1);
                    } else {
                        mf = moreFunctions.filter(f => f.name.indexOf('Unlock ') === -1);
                    }
                    return <MoreTools original={original} content={mf} />;
                },
                size: 30,
            });
        }

        if (hasShowInfo) {
            cols.push({
                id: 'info',
                header: '',
                cell: ({ row }) => (
                    <SvGraphics
                        onClick={() => showInfoBar(row.original)}
                        className={'table-more'}
                        svgname={`arrow-${t('arrow')}`}
                        style={{ width: '15px', height: '15px' }}
                    />
                ),
                size: 30,
            });
        }

        return cols;
    }, [v6Columns, hasMore, hasShowInfo, hasSelection, moreFunctions,
        selectAll, selected, data, showInfoBar, t, toggleAllRows, toggleRow]);

    const table = useReactTable({
        data,
        columns,
        pageCount: pageCount || 1,
        state: {
            pagination: {
                pageIndex: currentPage,
                pageSize: defaultPageSize,
            },
        },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handlePageChange = useCallback((page) => {
        getRequest(TabView, page);
        setCurrentPage(page);
    }, [getRequest, TabView]);

    return (
        <div className={'treatment-table'}>
            <div className={`ReactTable ${tableClassName || ''}`}>
                <div className="rt-table" role="grid">
                    <div className="rt-thead -header">
                        {table.getHeaderGroups().map(headerGroup => (
                            <div className="rt-tr" key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    const colDef = header.column.columnDef;
                                    const width = header.getSize();
                                    const headerContent = flexRender(colDef.header, header.getContext());

                                    return (
                                        <HeaderCell
                                            key={header.id}
                                            field={colDef.id || header.id}
                                            headerContent={headerContent}
                                            makeFilter={colDef.meta?.makeFilter}
                                            thClass={colDef.meta?.thClass}
                                            filterConverter={colDef.meta?.filterConverter}
                                            statuses={statuses}
                                            TabView={TabView}
                                            onFilterDataSelected={onFilterDataSelected}
                                            onOderByTable={onOderByFunction}
                                            style={{
                                                flex: `${width} 0 auto`,
                                                width: `${width}px`,
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="rt-tbody">
                        {data.length === 0 ? (
                            <NoDataConst />
                        ) : (
                            table.getRowModel().rows.map((row, index) => {
                                const original = row.original;
                                const locked = !isRowEnabled(original);
                                const rowStyle = {
                                    color: ((original.Enabled !== null && original.Enabled !== undefined) &&
                                        (original.Enabled === '1' || original.Enabled === 1))
                                        ? '#b4b4b4' : 'black',
                                };

                                return (
                                    <div className="rt-tr-group" key={row.id}>
                                        <div
                                            className={`rt-tr ${index % 2 === 0 ? '-even' : '-odd'} ${locked ? 'locked' : 'unlocked'}`}
                                            style={rowStyle}
                                        >
                                            {row.getVisibleCells().map(cell => {
                                                const w = cell.column.getSize();
                                                return (
                                                    <div
                                                        key={cell.id}
                                                        className={`rt-td ${cell.column.columnDef.meta?.className || ''}`}
                                                        style={{
                                                            flex: `${w} 0 auto`,
                                                            width: `${w}px`,
                                                        }}
                                                    >
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {showPagination && (
                    <Pagination
                        pages={pageCount}
                        page={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
}

NewPaginationTable.propTypes = {
    onSelectionChange: PropTypes.func,
    onFilterDataSelected: PropTypes.func,
    showInfoBar: PropTypes.func,
    moreFunctions: PropTypes.array,
    data: PropTypes.array.isRequired,
    statuses: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    hasMore: PropTypes.bool.isRequired,
    hasShowInfo: PropTypes.bool.isRequired,
};

export default NewPaginationTable;
